import React from "react"

/**
 * Focus management utility for forms with validation errors
 */

/**
 * Focuses on the first field with a validation error
 * @param errors - React Hook Form errors object
 * @param formRef - Optional form container reference for scoped searching
 */
export const focusFirstErrorField = (
  errors: Record<string, any>,
  formRef?: React.RefObject<HTMLElement>,
) => {
  // Get the first error field name
  const firstErrorField = Object.keys(errors)[0]

  if (!firstErrorField) {
    return
  }

  // Small delay to ensure the error state is rendered
  setTimeout(() => {
    const container = formRef?.current || document

    // Try to find the input by name attribute first
    let fieldElement = container.querySelector(
      `[name="${firstErrorField}"]`,
    ) as HTMLElement

    // If not found by name, try finding by data-testid or id
    if (!fieldElement) {
      fieldElement = container.querySelector(
        `[data-testid="${firstErrorField}"], #${firstErrorField}`,
      ) as HTMLElement
    }

    // For MUI components, we might need to find the actual input element
    if (!fieldElement) {
      // Look for input elements within a container that has the field name
      fieldElement = container.querySelector(
        `[data-field="${firstErrorField}"] input, [data-field="${firstErrorField}"] textarea`,
      ) as HTMLElement
    }

    // Try to find any input/textarea/select that matches the error field
    if (!fieldElement) {
      const allInputs = container.querySelectorAll(
        'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea, select, [role="combobox"]',
      )

      // Check if any input has a name that matches the error field
      for (const input of Array.from(allInputs)) {
        const inputElement = input as HTMLElement
        if (
          inputElement.getAttribute("name") === firstErrorField ||
          inputElement.closest("[data-field]")?.getAttribute("data-field") ===
            firstErrorField
        ) {
          fieldElement = inputElement
          break
        }
      }
    }

    if (fieldElement) {
      // Focus the element
      fieldElement.focus()

      // Scroll into view if needed
      fieldElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })
    } else {
      console.warn(`Could not find field element for error: ${firstErrorField}`)
    }
  }, 100)
}

/**
 * Hook for React Hook Form that automatically focuses on first error field
 * @param errors - React Hook Form errors object
 * @param isSubmitted - Whether the form has been submitted
 * @param formRef - Optional form container reference
 */
export const useFocusOnError = (
  errors: Record<string, any>,
  isSubmitted: boolean,
  formRef?: React.RefObject<HTMLElement>,
) => {
  // Use useEffect to focus on first error when errors change after submission
  React.useEffect(() => {
    if (isSubmitted && Object.keys(errors).length > 0) {
      focusFirstErrorField(errors, formRef)
    }
  }, [errors, isSubmitted, formRef])
}
